import { Box, Grid, MenuItem, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import { useSelector } from "react-redux";
import {
  RootState,
  useAppDispatch,
} from "../../../../app/features/store/store";
import FormikTextField from "../../../../components/common/formikFields/FormikTextField";
import SubmitModalWithFormik from "../../../../components/common/modalPopup/SubmitModalWithFormik";
import {
  LATE_CANCELLATION_TYPES,
  VARIABLE_RATE_TYPES,
} from "../RateCardConstants";
import { toast } from "react-toastify";
import { DateObject } from "react-multi-date-picker";
import { RatecardResponse } from "../dtos/RateCardResponse";
import getAxiosError from "../../../../app/api/axiosError";
import {
  mapFrequencyNumberToText,
  mapValuesUiRequestToUpdateRatecardValue,
} from "../utils/RatecardUtils";
import { mapValuesUiRequestToUpdateCustomFields } from "../utils/CustomFieldsUtills";
import { mapValuesUiRequestToUpdateFuelSurchargeFields } from "../utils/FuelSurchargeUtils";

import {
  mapUpdatePeakSurchargeToUiRequest,
  mapValuesUiRequestToUpdatePeakSurcharge,
} from "../utils/PeakSurchargeUtils";
import { getRouteRateValidationSchema } from "../RatecardValidations";
import {
  UpdateFuelSurchargeLookup,
  UpdateFuelSurchargeFields,
  UpdateRateCardRequest,
  UpdateCustomFields,
} from "../dtos/UpdateRateCardRequest";
import DiscountPremium from "./DiscountPremium";
import PeakSurcharge from "./PeakSurcharge";
import FuelSurcharge from "./FuelSurcharge";
import {
  clearCurrentRatecard,
  updateRatecard,
} from "../../../../app/features/ratecard/ratecardSlice";
import { useEffect, useState } from "react";
import { getRatecardById } from "../../../../app/features/ratecard/ratecardSlice";
import Loader from "../../../../components/common/loader/Loader";
import { mapSnakeCaseToCapitalCaseWithSpaces } from "../../../../components/common/utils/StringUtils";
import CustomFields from "./CustomFields";


// type to recieve parameter for this component to initialize
interface UpdateRouteRateModalProps {
  clientId: number;
  ratecardData: RatecardResponse | null;
  ratecardRequest: UpdateRateCardRequest;
  closeUpdateRouteRateModal: CallableFunction;
  onCompleted: CallableFunction;
}
export interface UpdatePeakSurchargeUIRequest {
  peakSurchargeId?: number;
  peakSurchargeType: string | null;
  peak_surcharge_date_range: DateObject[];
  peak_surcharge_value_percentage: number | undefined;
  peak_surcharge_value_dollar: number | undefined;
}

export interface UpdateRouteRateUIRequest {
  values_id?: number;
  baseRate: number | undefined;
  variableRate: number | undefined;
  variableRateType: string;
  routeMinimum: number | undefined;
  lateCancellationCharge: number | undefined;
  lateCancellationType: string;
  fuelType?: string;
  regionName?: string;
  //discount
  discountType?: string;
  discountMinVolumeRate?: number;
  discountFrequency?: string;
  discountUnitType?: string;
  discountValue?: number;
  // extra fields for the UI
  // show_discount?: any[];
  show_discount: boolean;
  discount_type?: string;
  discount_volume_min_unit?: number;
  discount_volume_frequency?: string;
  discount_volume_unit_type: string | null;
  discount_volume_value_percentage?: number;
  discount_volume_value_dollar?: number;
  discount_rate_type?: string;
  discount_rate_min_unit?: number;
  discount_rate_frequency?: string;
  discount_rate_unit_type: string | null;
  discount_rate_value_percentage?: number;
  discount_rate_value_dollar?: number;
  // FUEL SURCHARGE
  show_fuel_surcharge: boolean;
  show_custom_fields: boolean;
  rateCardCustomFieldsData: UpdateCustomFields[];
  fuelSurchargeFields: UpdateFuelSurchargeFields[];
  fuelSurchargeLookup: UpdateFuelSurchargeLookup[];
  lookup_file_upload: boolean;
  //peak surcharge
  show_peak_surcharge: boolean;
  peakSurcharge: UpdatePeakSurchargeUIRequest[];
}

// Update route rate modal component
const UpdateRouteRateModal = (props: UpdateRouteRateModalProps) => {
  const checkShowDiscount = () => {
    if (
      currentRatecard?.ratecardValues === undefined ||
      currentRatecard?.ratecardValues?.discountType === undefined
    )
      return false;
    return true;
  };

  // data to keep in store
  const dispatch = useAppDispatch();

  const { authResponse } = useSelector((state: RootState) => state.login);

  const [isDataFetching, setDataFetching] = useState(true);

  useEffect(() => {
    if (props.ratecardRequest.isActive) {
      getRatecardByIdData(); // Ensure this function does not alter dependencies
    }
  
    return () => {
      dispatch(clearCurrentRatecard()); // Ensure this action does not indirectly alter isActive
    };
  }, [props.ratecardRequest.isActive]);

  // get ratecard by id data
  const getRatecardByIdData = async () => {
    if (props.ratecardData) {
      try {
        const response = await dispatch(
          getRatecardById(props.ratecardData.ratecardId)
        ).unwrap();
        if (!response.isSuccess) {
          toast.error(response.message, {
            toastId: "getRatecardById",
          });
          return;
        }
      } catch (error) {
        const appError = getAxiosError(error);
        toast.error(appError.errorMessage, {
          toastId: "getRatecardByIdException",
        });
      } finally {
        setDataFetching(false);
      }
    }
  };

  const { isLoading: isCurrentRatecardLoading, currentRatecard } = useSelector(
    (state: RootState) => state.ratecard
  );

  const ratecardValuesData = currentRatecard?.ratecardValues;

  const initialValues: UpdateRouteRateUIRequest = {
    // VALUES
    values_id: ratecardValuesData?.id ?? undefined,
    baseRate: ratecardValuesData?.baseRate,
    variableRate: ratecardValuesData?.variableRate,
    routeMinimum: ratecardValuesData?.routeMinimum,
    lateCancellationCharge: ratecardValuesData?.lateCancellationCharge,
    variableRateType: ratecardValuesData?.variableRateType ?? "package",
    lateCancellationType:
      ratecardValuesData?.lateCancellationType ?? "per_route",
    fuelType: ratecardValuesData?.fuelType ?? "",
    regionName: ratecardValuesData?.regionName ?? "",
    discountType: checkShowDiscount()
      ? ratecardValuesData?.discountType
      : undefined,
    discountMinVolumeRate: checkShowDiscount()
      ? ratecardValuesData?.discountMinVolumeRate
      : undefined,
    discountFrequency: checkShowDiscount()
      ? mapFrequencyNumberToText(
          ratecardValuesData?.discountFrequency ?? undefined
        )
      : "",
    discountUnitType: checkShowDiscount()
      ? ratecardValuesData?.discountUnitType
      : undefined,
    discountValue: checkShowDiscount()
      ? ratecardValuesData?.discountValue
      : undefined,
    // discount - UI parameters
    show_discount: checkShowDiscount() ? true : false,
    discount_type: checkShowDiscount()
      ? ratecardValuesData?.discountType === "volume"
        ? "volume"
        : "rate"
      : "volume",
    // discount - UI parameters - volume
    discount_volume_min_unit:
      ratecardValuesData?.discountType === "volume"
        ? ratecardValuesData?.discountMinVolumeRate
        : undefined,
    discount_volume_frequency:
      checkShowDiscount() && ratecardValuesData?.discountType === "volume"
        ? mapFrequencyNumberToText(ratecardValuesData?.discountFrequency)
        : "",
    discount_volume_unit_type:
      ratecardValuesData?.discountType === "volume"
        ? ratecardValuesData?.discountUnitType
        : null,
    discount_volume_value_percentage:
      ratecardValuesData?.discountType === "volume" &&
      ratecardValuesData?.discountUnitType === "percentage"
        ? ratecardValuesData?.discountValue
        : undefined,
    discount_volume_value_dollar:
      ratecardValuesData?.discountType === "volume" &&
      ratecardValuesData?.discountUnitType === "value"
        ? ratecardValuesData?.discountValue
        : undefined,
    // discount - UI parameters - rate
    discount_rate_type: checkShowDiscount()
      ? ratecardValuesData?.discountType !== "volume"
        ? ratecardValuesData?.discountType
        : undefined
      : "base_rate",
    discount_rate_frequency:
      checkShowDiscount() && ratecardValuesData?.discountType !== "volume"
        ? mapFrequencyNumberToText(ratecardValuesData?.discountFrequency)
        : "",
    discount_rate_min_unit:
      ratecardValuesData?.discountType !== "volume"
        ? ratecardValuesData?.discountMinVolumeRate
        : undefined,
    discount_rate_unit_type:
      ratecardValuesData?.discountType !== "volume"
        ? ratecardValuesData?.discountUnitType ?? null
        : null,
    discount_rate_value_percentage:
      ratecardValuesData?.discountType !== "volume" &&
      ratecardValuesData?.discountUnitType === "percentage"
        ? ratecardValuesData?.discountValue
        : undefined,
    discount_rate_value_dollar:
      ratecardValuesData?.discountType !== "volume" &&
      ratecardValuesData?.discountUnitType === "value"
        ? ratecardValuesData?.discountValue
        : undefined,
    // FUEL SURCHARGE
    show_fuel_surcharge:
      currentRatecard?.ratecardFuelSurchargeFields &&
      currentRatecard?.ratecardFuelSurchargeFields.length > 0
        ? true
        : false,
    show_custom_fields: currentRatecard?.rateCardCustomFieldsData && currentRatecard?.rateCardCustomFieldsData.length> 0
    ? true
    : false,
    rateCardCustomFieldsData: currentRatecard?.rateCardCustomFieldsData ?? [],
    fuelSurchargeFields: currentRatecard?.ratecardFuelSurchargeFields ?? [],
    fuelSurchargeLookup: currentRatecard?.ratecardFuelSurchargeLookup ?? [],
    lookup_file_upload: false,
    // PEAK SURCHCARGE
    show_peak_surcharge:
      currentRatecard?.ratecardPeakSurcharges &&
      currentRatecard?.ratecardPeakSurcharges.length > 0
        ? true
        : false,
    peakSurcharge: mapUpdatePeakSurchargeToUiRequest(
      currentRatecard?.ratecardPeakSurcharges
    ),
  };

  const handleSubmit = async (
    addRatecardValuesUIRequest: UpdateRouteRateUIRequest,
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    try {
      if (props.ratecardData) {
        const updateRateCardValues = mapValuesUiRequestToUpdateRatecardValue(
          addRatecardValuesUIRequest
        );

        const updateRateCardFuelSurchargeFields =
          mapValuesUiRequestToUpdateFuelSurchargeFields(
            addRatecardValuesUIRequest,
            authResponse?.userSettings?.userName
          );
          const updateRateCardCustomFields =
          mapValuesUiRequestToUpdateCustomFields(
            addRatecardValuesUIRequest,
           // authResponse?.userSettings?.userName
          );
   

        const updateRateCardFuelSurchargeLookup =
          addRatecardValuesUIRequest.lookup_file_upload &&
          addRatecardValuesUIRequest.fuelSurchargeLookup.length > 0
            ? addRatecardValuesUIRequest.fuelSurchargeLookup
            : [];

        const updateRateCardPeakSurcharge =
          mapValuesUiRequestToUpdatePeakSurcharge(addRatecardValuesUIRequest);

        const updateRatecardRequest: UpdateRateCardRequest = {
          clientId: props.ratecardRequest.clientId,
          isActive: props.ratecardRequest.isActive,
          ratecardClientMappings: props.ratecardRequest.ratecardClientMappings,
          ratecardValues: updateRateCardValues,
          rateCardCustomFieldsData :updateRateCardCustomFields,
          ratecardFuelSurchargeFields: updateRateCardFuelSurchargeFields,
          ratecardFuelSurchargeLookup: updateRateCardFuelSurchargeLookup,
          ratecardPeakSurcharges: updateRateCardPeakSurcharge,
        };

        const response = await dispatch(
          updateRatecard({
            ratecardId: props.ratecardData.ratecardId,
            updateRatecardData: updateRatecardRequest,
          })
        ).unwrap();
        if (!response.isSuccess) {
          toast.error(response.message, {
            toastId: "updateRatecard",
          });
          toast.success("Ratecard has been updated successfully.");
        }
      }
    } catch (error) {
      const appError = getAxiosError(error);
      if (appError.code === "400") {
        toast.error("Some error occured", {
          toastId: "updateRatecardException",
        });
      } else {
        toast.error(appError.errorMessage, {
          toastId: "updateRatecardException",
        });
      }
    } finally {
      setSubmitting(false);
      props.onCompleted();
    }
    return true;
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={getRouteRateValidationSchema()}
        onSubmit={(values: UpdateRouteRateUIRequest, { setSubmitting }) => {
          setSubmitting(true);
          handleSubmit(values, setSubmitting);
        }}
      >
        {({
          handleChange,
          handleBlur,
          values,
          touched,
          setFieldValue,
          isSubmitting,
        }) => {
          return (
            <SubmitModalWithFormik
              title={"Update Route Rate Details"}
              closeSubmitModal={props.closeUpdateRouteRateModal}
              childErrorMessage={""}
              allowSubmit={false}
              size="lg"
              isLoading={false}
              submitButtonName="Update"
              closeModalAfterSubmit={false}
              allowSubmitWithoutTouch={true}

            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Loader
                  isLoading={
                    isCurrentRatecardLoading || isSubmitting || isDataFetching
                  }
                />
                <Form>
                  <Grid container rowSpacing={2} columnSpacing={2}>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <Grid container alignItems={"center"} columnSpacing={2}>
                        <Grid item xs={6}>
                          <Typography>Base Rate</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <FormikTextField
                            margin="normal"
                            id="baseRate"
                            name="baseRate"
                            label=""
                            size="small"
                            type="number"
                            InputProps={{ inputProps: { min: 0 } }}
                            fullWidth
                            variant={"outlined"}
                            required
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <Grid container alignItems={"center"} columnSpacing={2}>
                        <Grid item xs={6}>
                          <Typography>Variable Rate</Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <FormikTextField
                            margin="normal"
                            id="variableRate"
                            name="variableRate"
                            label=""
                            type="number"
                            InputProps={{ inputProps: { min: 0 } }}
                            size="small"
                            fullWidth
                            variant={"outlined"}
                            required
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <FormikTextField
                            select
                            margin="normal"
                            id="variableRateType"
                            name="variableRateType"
                            label="Variable Rate Type"
                            size="small"
                            fullWidth
                            variant={"outlined"}
                            required
                          >
                            {VARIABLE_RATE_TYPES.map((state, index) => (
                              <MenuItem key={index} value={state}>
                                {`Per ${state.replace("_", " ")}`}
                              </MenuItem>
                            ))}
                          </FormikTextField>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <Grid container alignItems={"center"} columnSpacing={2}>
                        <Grid item xs={6}>
                          <Typography>Route Minimum</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <FormikTextField
                            margin="normal"
                            id="routeMinimum"
                            name="routeMinimum"
                            label=""
                            size="small"
                            type="number"
                            InputProps={{ inputProps: { min: 0 } }}
                            fullWidth
                            variant={"outlined"}
                            required
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <Grid container alignItems={"center"} columnSpacing={2}>
                        <Grid item xs={6}>
                          <Typography>Late Cancellation Charge</Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <FormikTextField
                            margin="normal"
                            id="lateCancellationCharge"
                            name="lateCancellationCharge"
                            label=""
                            size="small"
                            type="number"
                            InputProps={{ inputProps: { min: 0 } }}
                            fullWidth
                            variant={"outlined"}
                            required
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <FormikTextField
                            select
                            margin="normal"
                            id="lateCancellationType"
                            name="lateCancellationType"
                            label="Late Cancellation Type"
                            size="small"
                            fullWidth
                            variant={"outlined"}
                            required
                          >
                            {LATE_CANCELLATION_TYPES.map((type, index) => (
                              <MenuItem key={index} value={type}>
                                {mapSnakeCaseToCapitalCaseWithSpaces(type)}
                              </MenuItem>
                            ))}
                          </FormikTextField>
                        </Grid>
                      </Grid>
                    </Grid>

                    <CustomFields
                    values = {values}
                    touched = {touched}
                    handleChange={handleChange}
                    handleBlur = {handleBlur}
                    setFieldValue={setFieldValue}
                    />

                    {/* Fuel Surcharge */}
                    <FuelSurcharge
                      values={values}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                    />

                

                    {/* Discount Premium */}
                    <DiscountPremium
                      values={values}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                    />

                    {/* Peak Surcharge */}
                    <PeakSurcharge
                      values={values}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                    />
                  </Grid>
                </Form>
              </Box>
            </SubmitModalWithFormik>
          );
        }}
      </Formik>
    </>
  );
};

export default UpdateRouteRateModal;
